/**
 * Rule 16 Compliance API client
 *
 * Route contract notes:
 * - Base ENDPOINT = '/investigations'
 * - API client prepends '/api' base URL -> final paths: '/api/investigations/...'
 * - Backend mounts compliance router at '/api' prefix (main.py line 505)
 * - No double /api prefix - paths are correctly structured
 */

import { apiClient } from './client';
import type {
  Rule16Status,
  ChecklistItem,
  RequirementTypeInfo,
  FlagDocumentRequest,
  CreateRequirementRequest,
  UpdateRequirementRequest,
  FileObjectionRequest,
  ComplianceRequirement,
  SummaryByType,
  DisclosureLogExport,
  EvidenceGapReport,
  PresetInfo,
  PresetInitResult,
  RequirementStatus,
  RequirementType,
} from './compliance-types';

const ENDPOINT = '/investigations';

export const complianceApi = {
  /**
   * Get Rule 16 compliance status for an investigation
   */
  getStatus: async (investigationId: string): Promise<Rule16Status> => {
    const response = await apiClient.get<Rule16Status>(
      `${ENDPOINT}/${investigationId}/rule16/status`
    );
    return response.data;
  },

  /**
   * Get disclosure checklist
   */
  getChecklist: async (
    investigationId: string,
    params?: {
      status?: RequirementStatus;
      requirement_type?: RequirementType;
    }
  ): Promise<ChecklistItem[]> => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.requirement_type) searchParams.append('requirement_type', params.requirement_type);

    const queryString = searchParams.toString();
    const url = `${ENDPOINT}/${investigationId}/rule16/checklist${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<ChecklistItem[]>(url);
    return response.data;
  },

  /**
   * Flag a document for disclosure
   */
  flagDocument: async (
    investigationId: string,
    request: FlagDocumentRequest
  ): Promise<ComplianceRequirement> => {
    const response = await apiClient.post<ComplianceRequirement>(
      `${ENDPOINT}/${investigationId}/rule16/flag`,
      request
    );
    return response.data;
  },

  /**
   * Create a compliance requirement
   */
  createRequirement: async (
    investigationId: string,
    request: CreateRequirementRequest
  ): Promise<ComplianceRequirement> => {
    const response = await apiClient.post<ComplianceRequirement>(
      `${ENDPOINT}/${investigationId}/rule16/requirements`,
      request
    );
    return response.data;
  },

  /**
   * Update a compliance requirement
   */
  updateRequirement: async (
    investigationId: string,
    requirementId: string,
    request: UpdateRequirementRequest
  ): Promise<ComplianceRequirement> => {
    const response = await apiClient.put<ComplianceRequirement>(
      `${ENDPOINT}/${investigationId}/rule16/requirements/${requirementId}`,
      request
    );
    return response.data;
  },

  /**
   * Mark a requirement as disclosed
   */
  markDisclosed: async (
    investigationId: string,
    requirementId: string,
    disclosedTo: string,
    method?: string
  ): Promise<ComplianceRequirement> => {
    const searchParams = new URLSearchParams();
    searchParams.append('disclosed_to', disclosedTo);
    if (method) searchParams.append('method', method);

    const response = await apiClient.post<ComplianceRequirement>(
      `${ENDPOINT}/${investigationId}/rule16/requirements/${requirementId}/disclose?${searchParams.toString()}`
    );
    return response.data;
  },

  /**
   * File an objection
   */
  fileObjection: async (
    investigationId: string,
    requirementId: string,
    request: FileObjectionRequest
  ): Promise<ComplianceRequirement> => {
    const response = await apiClient.post<ComplianceRequirement>(
      `${ENDPOINT}/${investigationId}/rule16/requirements/${requirementId}/object`,
      request
    );
    return response.data;
  },

  /**
   * Get summary by type
   */
  getSummaryByType: async (investigationId: string): Promise<SummaryByType> => {
    const response = await apiClient.get<SummaryByType>(
      `${ENDPOINT}/${investigationId}/rule16/summary`
    );
    return response.data;
  },

  /**
   * Export disclosure log
   */
  exportDisclosureLog: async (
    investigationId: string,
    format: 'json' = 'json'
  ): Promise<DisclosureLogExport> => {
    const response = await apiClient.get<DisclosureLogExport>(
      `${ENDPOINT}/${investigationId}/rule16/export?format=${format}`
    );
    return response.data;
  },

  /**
   * Get requirement types with legal descriptions
   */
  getRequirementTypes: async (
    investigationId: string
  ): Promise<{ types: RequirementTypeInfo[] }> => {
    const response = await apiClient.get<{ types: RequirementTypeInfo[] }>(
      `${ENDPOINT}/${investigationId}/rule16/requirement-types`
    );
    return response.data;
  },

  /**
   * Detect missing evidence categories (S3-02)
   */
  detectGaps: async (
    investigationId: string,
    preset?: string
  ): Promise<EvidenceGapReport> => {
    const response = await apiClient.get<EvidenceGapReport>(
      `${ENDPOINT}/${investigationId}/rule16/gaps`,
      { params: preset ? { preset } : undefined }
    );
    return response.data;
  },

  /**
   * List available criminal defense presets (S3-04)
   */
  listPresets: async (): Promise<{ presets: PresetInfo[] }> => {
    const response = await apiClient.get<{ presets: PresetInfo[] }>(
      '/rule16/presets'
    );
    return response.data;
  },

  /**
   * Initialize checklist from a preset (S3-04)
   */
  initializePreset: async (
    investigationId: string,
    presetName: string,
    skipExisting: boolean = true
  ): Promise<PresetInitResult> => {
    const params = new URLSearchParams();
    params.append('skip_existing', String(skipExisting));

    const response = await apiClient.post<PresetInitResult>(
      `${ENDPOINT}/${investigationId}/rule16/presets/${presetName}?${params.toString()}`
    );
    return response.data;
  },
};

export default complianceApi;
